// Contrato de Análisis de ventas — Portal Admin VentasLuga (F3 · pista Retail 🔵).
//
// ✅ 2026-08-07: este archivo dejó de ser una propuesta. El endpoint 29a se construyó y desplegó
// (`GET /backoffice/sales/analytics` de `retail-wizard-business`) y lo que sigue es el ESPEJO de su
// respuesta real, no la forma que la UI había pedido. Donde el silo decidió distinto, mandó el silo
// y el adapter vive en `analytics.client.ts`.
//
// Las tres diferencias que cambiaron la pantalla, y por qué:
//
//   1. NO HAY RANGO LIBRE. El endpoint no acepta `from`/`to`: devuelve los TRES rangos del selector
//      (hoy · 7d · 30d) calculados de la MISMA lectura. Aceptar una ventana libre habría devuelto
//      números de un periodo que el endpoint no leyó. La UI cambió sus dos inputs de fecha por tres
//      pills y cambiar de pill ya no pega a la API.
//   2. LA MATRIZ NO LLEGA PLANA. Llega por fila (`financierMix[].cells`), que era el objetivo del
//      diseño original —no transmitir los ceros— resuelto de otra forma.
//   3. LA ROTACIÓN NO ESTÁ ACÁ. Es el endpoint 29b de `retail-catalog-business`
//      (`GET /backoffice/inventory/rotation`), porque el inventario no vive en el wizard. La pantalla
//      hace las dos llamadas y cruza por `storeId`.
//
// Y tres campos que la UI no había pedido y sí tiene que pintar: `orphanReferrals`,
// `truncatedCurrent`/`truncatedPrevious` y `lostOpportunity`. Los tres existen para que la pantalla
// no mienta cuando el dato está incompleto — ignorarlos sería pintar un número parcial como total.
//
// DINERO: centavos enteros.
//
// 🔴 **PORCENTAJES: el silo los manda en ESCALA DE PORCENTAJE y redondeados — `42` es 42 %, NO 0.42.**
// El contrato original de esta pantalla decía «fracción» porque se escribió antes que el endpoint;
// el silo hace `Math.round(x * 100)` (ver `changePct` y `sureKeepSharePct` en `salesAnalytics.ts`).
// Multiplicar de nuevo en la UI producía `+10000%` donde iba `+100%` y `6000% SK` donde iba `60%`.
// Los campos que terminan en `Pct` NO se vuelven a escalar al pintarlos.

import type { Financier } from '../../retail-org/backoffice';

/** Los tres rangos del selector. Las llaves son las del silo, no se traducen. */
export type AnalyticsRangeKey = 'today' | '7d' | '30d';

export const ANALYTICS_RANGE_KEYS: AnalyticsRangeKey[] = ['today', '7d', '30d'];

export const ANALYTICS_RANGE_LABEL: Record<AnalyticsRangeKey, string> = {
    today: 'Hoy',
    '7d': '7 días',
    '30d': '30 días',
};

/** Una celda del mix: cuántas piezas vendió esa tienda con esa financiera. */
export interface FinancierCell {
    financier: Financier;
    units: number;
}

/**
 * Renglón del mix por tienda × financiera. Las celdas llegan SOLO donde hubo venta; la UI completa
 * los ceros al pivotar.
 */
export interface StoreFinancierMixRow {
    storeId: string;
    storeName: string | null;
    zoneName: string | null;
    cells: FinancierCell[];
    totalUnits: number;
}

/**
 * Renglón de «cada tienda contra su periodo anterior».
 *
 * `previousUnits` en 0 con `unitsChangePct` en `null` NO es lo mismo que no aparecer: la tienda que
 * vendió antes y hoy no vende TIENE que salir, porque es la pregunta entera de la pantalla.
 */
export interface StoreCohortRow {
    storeId: string;
    storeName: string | null;
    units: number;
    cents: number;
    previousUnits: number;
    previousCents: number;
    /** Escala de PORCENTAJE, ya redondeada: `42` = 42 %. `null` si no hay base con qué comparar. */
    unitsChangePct: number | null;
    /** Idem — escala de porcentaje, no fracción. */
    centsChangePct: number | null;
}

/** Renglón del top de modelos. `sureKeepSharePct` es cuánto de ese modelo financió SureKeep. */
export interface TopSkuRow {
    /** El silo no manda ventas sin equipo; si llegara una en `null`, la pantalla la descarta. */
    sku: string | null;
    productBrand: string | null;
    productModel: string | null;
    units: number;
    sureKeepUnits: number;
    /** Escala de PORCENTAJE: `60` = 60 %. `null` cuando no hubo piezas con qué calcularlo. */
    sureKeepSharePct: number | null;
}

/** Renglón del ranking de vendedores y del mes-contra-mes. */
export interface SellerRankRow {
    sellerRetailUserId: string;
    sellerName: string | null;
    units: number;
    cents: number;
    previousUnits: number | null;
    previousCents: number | null;
    /** Escala de PORCENTAJE, ya redondeada. */
    unitsChangePct: number | null;
    centsChangePct: number | null;
}

/** Los cuatro tableros que SÍ siguen al selector de rango. */
export interface AnalyticsRangeBlocks {
    financierMix: StoreFinancierMixRow[];
    storeCohort: StoreCohortRow[];
    topSkus: TopSkuRow[];
    sellerRanking: SellerRankRow[];
    /**
     * Ventas derivadas a un tercero SIN proveedor registrado, EN ESTE RANGO.
     *
     * 🔴 Existe porque el contador global (`AnalyticsResponse.orphanReferrals`) cubre los 60 días de
     * la lectura, y pintarlo bajo un tablero de un día decía «5 ventas» cuando ese día hubo cero.
     * Este es el que la UI debe usar: el que corresponde a lo que el operador está mirando.
     */
    orphanReferrals: number;
    /**
     * La ventana que el silo efectivamente leyó, en ISO — **cortada en medianoche de CDMX**, no de
     * UTC. La UI la muestra sin adivinar: la etiqueta del pill promete «mes en curso» y la ventana
     * real es móvil de 30 días, así que sin esto el operador lee un número de agosto que arranca en
     * julio.
     */
    from: string;
    to: string;
}

export interface AnalyticsResponse {
    /** Los TRES rangos, de la misma lectura. Cambiar de pill NO vuelve a pegarle a la API. */
    ranges: Record<string, AnalyticsRangeBlocks>;
    /** Vendedores mes contra mes. Ventanas FIJAS (30d vs 30-60d): no sigue al selector. */
    sellerMonthOverMonth: SellerRankRow[];
    /**
     * `EXTERNAL_REFERRAL` sin proveedor, en los 60 días COMPLETOS de la lectura.
     *
     * ⚠️ Para pintar bajo un tablero, usa `ranges[x].orphanReferrals`, que sí corresponde al periodo
     * que el operador tiene seleccionado. Este global sirve para una nota de contexto, no para una
     * cifra al pie de un rango.
     *
     * Ya NO entran al `financierMix`: el silo las mandaba a `NONE`, el mismo bucket que el contado, e
     * inflaban la columna «Ctd». No son de contado y no se sabe con quién financiaron.
     */
    orphanReferrals: number;
    /**
     * Truncado POR VENTANA. Si la actual se cortó y la anterior no, todos los `changePct` son
     * basura, no «aproximados»: la UI tiene que poder distinguirlo y decirlo.
     */
    truncatedCurrent: boolean;
    truncatedPrevious: boolean;
    /**
     * El tablero «Rechazos SK que sí compraron con otra financiera» necesita las solicitudes de
     * crédito rechazadas con su motivo — dominio de la pista SOFOM 🟣, cuyos repos no existen. Viaja
     * declarado para que la pantalla pinte el hueco con su razón en vez de un cero que se leería
     * como «no hubo rechazos».
     */
    lostOpportunity: { available: false; reason: string };
}

export interface AnalyticsApi {
    /** Sin parámetros: el retailer sale del token y los tres rangos vienen siempre juntos. */
    getAnalytics(): Promise<AnalyticsResponse>;
}
